import { createContext, use } from 'react'
import type { ScribeSourceLanguage } from '@behuman/core-contract'
import type { VideoClient } from '@zoom/videosdk'
import type { ScribeState, InterruptedSession, PatientPresence } from './callSessionReducer'

/**
 * In-call chat message surfaced to consumers via `useCallSession()`.
 * Mirrors Zoom's `ChatMessage` shape but only carries the fields we
 * actually render (id, sender userId + name, message text, timestamp).
 */
export interface CallChatMessage {
  id: string
  senderUserId: number | undefined
  senderName: string
  message: string
  timestamp: number
  /** True when the local user sent it (so the UI can right-align). */
  isOwn: boolean
}

/**
 * Parameters required to allocate the Zoom Video SDK client, fetch a signature,
 * and join a live session. Centralised so both the new side-panel "New
 * Encounter" flow and the legacy `/call/:encounterId` route call into the
 * provider through a single shape.
 */
export interface JoinSessionParams {
  /** FHIR Encounter id (or a stand-in id while the encounter is still being created). */
  encounterId: string
  /**
   * Zoom session name — must match between provider and patient sides. The
   * booking flow stores this on the Encounter as a `teleconference-session`
   * extension and CallPage reads it at join time; ad-hoc chart encounters
   * (no booking, no extension) fall back to the patient id.
   */
  sessionName: string
  /** Display name surfaced to the other participants in the Zoom session. */
  userName: string
  /**
   * Interrupted-visit recovery. When set, {@link CallSessionContextValue.joinSession}
   * rejoins the SAME per-encounter Zoom room AND adopts this recovered
   * server-issued scribe session id rather than minting a new one — the JOIN
   * reducer action seeds `scribeSessionId` with it and flips `scribeState` to
   * 'streaming' so `<ZoomPanel>` skips its auto-start and continues live
   * transcription into the same session. Omit for a normal fresh join.
   */
  resumeSessionId?: string
}

/**
 * Discriminated outcome of {@link CallSessionContextValue.joinSession}.
 *
 *  - `'joined'` — the Zoom join committed (`isJoined` flipped true).
 *  - `'failed'` — the join was attempted but errored (dead room, signature
 *    fetch failure, SDK init/join rejection). The interrupted-visit Resume
 *    flow degrades to "Finish / Discard" on this.
 *  - `'busy'`   — a join was already in flight, so this call was a safe no-op.
 *    This is NOT a dead room — the Resume flow must NOT degrade on it.
 *  - `'already-joined'` — PD-1439: another browser tab holds the server-side
 *    join claim for this encounter. Zoom was never touched; the caller tells the
 *    doctor to use the tab they already have open.
 */
export type JoinSessionResult = 'joined' | 'failed' | 'busy' | 'already-joined'

export interface CallSessionContextValue {
  /** Encounter the doctor is currently in a call for, or null if no call active. */
  currentEncounterId: string | null
  /** True once the Zoom client has been instantiated for this encounter. */
  isJoined: boolean
  /**
   * Opaque server-issued scribe session id (UUID) returned by
   * `POST /scribe/sessions/:encounterId/start`. Set by `useStartScribe`,
   * cleared by `useFinalizeScribeSession` and by `leaveSession`. Null until
   * the doctor explicitly starts the scribe.
   */
  scribeSessionId: string | null
  /** Scribe-controls lifecycle state — single source of truth across all panel children. */
  scribeState: ScribeState
  /** PD-1600 — the visit's spoken language; pins live transcription and rides to the server on start. */
  encounterLanguage: ScribeSourceLanguage
  setEncounterLanguage: (language: ScribeSourceLanguage) => void
  /**
   * Live presence of the remote patient participant, roster-derived from the
   * Zoom client. Drives the presence pill, waiting-room copy, the LIVE /
   * "Waiting for Patient" and TRANSCRIBING / "Scribe Ready" gates, the session
   * status bar, and the primary-video swap. `not-joined` until a remote
   * participant is first seen; `disconnected` after one leaves.
   */
  patientPresence: PatientPresence
  /**
   * Live presence of a non-patient staff participant (care coordinator),
   * roster-derived. Drives the coordinator video tile + the status-bar
   * Coordinator dot. `not-joined` until such a participant is first seen.
   */
  staffPresence: PatientPresence
  /** Staff participant's Zoom display name, for the tile chip. Retained across a disconnect. */
  staffDisplayName: string | null
  /**
   * Classify a roster participant by identity: `'self'` for the local user,
   * `'patient'` for a guest identity (`guest-…`), `'staff'` for any other
   * Practitioner. Drives video-tile routing and per-role caption attribution.
   * Unknown / unresolved participants classify as `'patient'` (preserves the
   * prior two-party behavior).
   */
  classifyCallParticipant: (userId: number) => 'self' | 'patient' | 'staff'
  /** Lazy-instantiated Zoom Video SDK client. Null until {@link joinSession} runs. */
  zoomClient: typeof VideoClient | null
  /** True when the local audio track is currently muted. */
  isAudioMuted: boolean
  /** True when the local video track is currently stopped. */
  isVideoMuted: boolean
  /**
   * Allocate the Zoom client, fetch a signature, join the session, start
   * local audio/video, and mark the encounter as active. Idempotent — a
   * second call while joining is a safe no-op.
   *
   * Resolves to a discriminated {@link JoinSessionResult}:
   *   - `'joined'` when the join committed (`isJoined` flipped true),
   *   - `'failed'` when the join was attempted but errored (dead room),
   *   - `'busy'` when a join was already in flight (safe no-op).
   *
   * The interrupted-visit Resume flow uses this to distinguish a dead call
   * (`'failed'` → degrade to "Finish / Discard") from a benign re-entrancy
   * no-op (`'busy'` → do NOT degrade). Existing callers that ignore the
   * return value are unaffected.
   */
  joinSession: (params: JoinSessionParams) => Promise<JoinSessionResult>
  /**
   * Leave the Zoom session, detach local video, and clear the active
   * encounter. Safe to call multiple times and safe to call before any join
   * has happened.
   */
  leaveSession: () => Promise<void>
  /**
   * Toggle the local audio track between muted / unmuted. Mirrors the
   * resulting state on {@link isAudioMuted}. No-op when no Zoom session is
   * active.
   */
  toggleMute: () => Promise<void>
  /**
   * Toggle the local video track between started / stopped. Mirrors the
   * resulting state on {@link isVideoMuted}. No-op when no Zoom session is
   * active.
   */
  toggleVideo: () => Promise<void>
  /**
   * Tri-state — scribe runs only on the treating physician (strictly
   * MEDICAL_DOCTOR): `null` = unresolved, `false` = no, `true` = yes.
   */
  isLocalUserPhysician: boolean | null
  /**
   * Tri-state — `true` when the local user is a manager (care coordinator
   * 303129000 / call-center manager 224565004), who is barred from the
   * transcript, scribe controls, and progress-note create/edit. `null` =
   * unresolved, `false` = not a manager. Gates fail OPEN on null/false.
   */
  isLocalUserManager: boolean | null
  /**
   * True once the local user's role resolves to a virtual-background-eligible
   * clinical credential (MEDICAL_DOCTOR / HOSPITAL_MANAGER). The §4
   * "Background" control only renders when this is true — false until the role
   * resolves on join, and for non-clinical profiles.
   */
  canUseVirtualBackground: boolean
  /** True when the branded virtual background is currently applied. */
  isVirtualBackgroundOn: boolean
  /**
   * Toggle the branded BeHuman virtual background on/off on the local video.
   * Optimistic-flip-only when no Zoom session is active; best-effort when the
   * SDK can't apply it (e.g. a browser that isn't cross-origin-isolated, where
   * VB is unsupported). Mirrors the resulting state on {@link isVirtualBackgroundOn}.
   */
  toggleVirtualBackground: () => Promise<void>
  /**
   * Register the DOM element that should host the remote-participant video
   * stream. `<PatientVideoFrame>` claims this on mount and releases it on
   * unmount; the provider re-runs `renderVideo` against the new target when
   * it changes mid-call.
   */
  attachRemoteVideoContainer: (el: HTMLElement | null) => void
  /**
   * Register the DOM element that should host the STAFF (care coordinator)
   * video stream — the small tile in `<PatientVideoFrame>`. Mounts only while a
   * staff participant is connected; the provider replays that participant's
   * video into it on registration and releases it on `null`.
   */
  attachStaffVideoContainer: (el: HTMLElement | null) => void
  /**
   * Register the DOM element that should host the LOCAL user's self-view
   * video (the PiP tile in `<PatientVideoFrame>`). The provider attaches the
   * doctor's own Zoom track into it — the remote pipeline deliberately bails
   * on the local user, so this is the only place the doctor's video renders.
   * Registering a container mid-call re-attaches immediately when the camera
   * is on; pass `null` on unmount to release.
   */
  attachSelfVideoContainer: (el: HTMLElement | null) => void
  /**
   * Pure state-flip alternative to {@link joinSession} for callers that own
   * their own Zoom client (today: legacy CallPage paths that have not yet
   * been folded into the provider). Dispatches JOIN and sets the encounter
   * id without allocating a second Zoom client. Pair with
   * {@link markSessionEnded} on the way out.
   */
  markSessionActive: (encounterId: string) => void
  /** State-flip companion to {@link markSessionActive}. Dispatches RESET only. */
  markSessionEnded: () => void
  /**
   * Persist the active scribe session id in provider state so any consumer
   * (recording badge, finalize hook, future generate-draft trigger) can
   * read it from a single source of truth. Pass `null` to clear.
   */
  setScribeSessionId: (sessionId: string | null) => void
  /** Dispatch a new scribe-controls state. */
  setScribeState: (next: ScribeState) => void
  /**
   * Recoverable scribe session discovered on mount (interrupted-visit
   * recovery), or null when none. The chart-scoped `<InterruptedResumePanel>`
   * reads this. Resume/Finish are NOT provider methods — they're navigation to
   * `/call` (handled by the chart host), since the call surface + its
   * recovered-captions / finalize wiring live on the `/call` route. The only
   * recovery action the provider owns is {@link discardInterrupted}.
   */
  interruptedSession: InterruptedSession | null
  /**
   * Discard the interrupted session: confirm → DELETE the Redis-backed session
   * → clear the marker → CLEAR_INTERRUPTED (status returns to idle, panel
   * unmounts). Best-effort: even if the DELETE fails the local state is cleared
   * so the doctor isn't trapped.
   */
  discardInterrupted: () => Promise<void>
  /**
   * In-call chat messages, oldest-first. Hydrated from Zoom's
   * `chat-on-message` event. Reset on `joinSession` / `leaveSession`.
   */
  chatMessages: CallChatMessage[]
  /**
   * Send a chat message to all participants in the current Zoom session.
   * Promise resolves once Zoom acknowledges; the resulting message is
   * also pushed locally so the doctor sees their own message immediately
   * without a server round-trip.
   */
  sendChatMessage: (text: string) => Promise<void>
  /**
   * "Ask BeAssistant" rail thread for the current call — the oRPC threadPath
   * of the patient-pinned conversation, or null before the first question.
   * Lives here (not in ZoomPanel) because the rail unmounts on route changes
   * while the call survives. Reset on `joinSession` / `leaveSession`.
   */
  assistantThreadPath: string[] | null
  setAssistantThreadPath: (path: string[] | null) => void
}

export const CallSessionContext = createContext<CallSessionContextValue | null>(null)

/**
 * Read the call-session context. Must be called inside <CallSessionProvider>
 * (hoisted in AppRoutes around the user + super-admin trees).
 */
export function useCallSession(): CallSessionContextValue {
  const context = use(CallSessionContext)
  if (!context) {
    throw new Error('useCallSession must be used within a CallSessionProvider')
  }
  return context
}
